import axios from "axios";
import Table from "components/DataTable/Table";
import Spinner from "components/Spinner";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  faFileExcel,
  faFileArchive,
  faFilePdf,
  faFileText,
  faFileWord,
  faFileZipper,
  faFileImage,
  faCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";
import { faFileImage as faFileImageRegular } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setNotification } from "features/Notification/notificationSlice";

function MProductFiles({ selected, isUpdateMode }) {
  const user = useSelector((state) => state.user?.userInfo);

  const dispatch = useDispatch();

  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteID, setDeleteID] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const [mProductFiles, setMProductFiles] = useState(null);

  const [selectedFile, setSelectedFile] = useState(false);
  const [newFileName, setNewFileName] = useState("");
  const [showRenameModal, setShowRenameModal] = useState(false);

  const [loading, setLoading] = useState(false);

  const [files, setFiles] = useState(null);
  const [uploadedIDList, setUploadedIDList] = useState([]);
  const [lastSortId, setLastSortId] = useState(null);
  const handleFileChange = (event) => {
    let allFiles = [];
    Array.from(event.target.files).forEach((i, index) => {
      let item = { id: index, file: event.target.files[index] };
      allFiles.push(item);
    });

    setFiles(allFiles);
  };

  /*   All Functions
   ********************************************* */

  const getMProductFiles = async () => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "manufacturer/product-files/listfiles",
        {
          id: selected?.id,
        },
        { headers: authHeader() },
      );

      setMProductFiles(data);
      setLoading(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  const uploadFiles = async (file, sortID) => {
    const formData = new FormData();
    formData.append("file", file?.file);
    formData.append(
      "folder",
      `kms/manufacturer/product/${user?.companyId}/${selected?.id}/files`,
    );
    formData.append("sortId", sortID + 1);
    formData.append("companyId", user?.companyId);
    formData.append("productId", selected?.id);

    if (file?.file == null) {
      dispatch(
        setNotification({
          message: "No file selected for upload.",
          type: "error",
        }),
      );
      return;
    }

    await API.post("/manufacturer/product-files/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setUploadedIDList((prev) => prev?.concat(file?.id));
  };

  // Adds Button Function
  const handleUploadFiles = async (e) => {
    e.preventDefault();
    setLoading(true);

    const uploadPromises = files.map((file, index) =>
      uploadFiles(file, lastSortId + index),
    );

    try {
      // Wait for all file uploads to complete
      setLoading(true);
      await Promise.all(uploadPromises);
    } catch (error) {
      dispatch(
        setNotification({
          message: error.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
    getMProductFiles();
    setFiles(null);
    setUploadedIDList([]);
    setLastSortId(null);
    setLoading(false);
    setShowAddModal(false);
  };

  const downloadFile = (fileKey) => {
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URI}/aws/images`, {
        key: fileKey,
      })
      .then((response) => {
        const url = response.data[0];
        setLoading(false);
        const link = document.createElement("a");
        link.href = url;
        // Opens in a new tab (user, 2026-11-02) instead of downloading.
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })

      .catch((error) => {
        console.log("Error downloading file:", error);
      });
  };

  const deleteFile = async ({ id, file }) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer/product-files/delete-file",
        {
          file: file,
          id: id,
        },
        { headers: authHeader() },
      );
      dispatch(
        setNotification({
          message: data,
          type: "success",
        }),
      );
      getMProductFiles();
      setLoading(true);
      setShowDeleteModal();
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  const getFileName = (fileName) => {
    return fileName?.split("/")[fileName?.split("/").length - 1];
  };

  const fileNameDisplay = (fileName) => {
    let _fileName = getFileName(fileName);
    if (!_fileName) return "";

    // trim fileName to 20 characters and add ellipsis if longer but always show the extension
    if (!_fileName) return "";
    const fileParts = _fileName.split(".");
    const fileNameWithoutExt = fileParts.slice(0, -1).join(".");
    const fileExt = fileParts[fileParts.length - 1];

    if (fileNameWithoutExt.length > 50) {
      return `${fileNameWithoutExt.slice(0, 50)}...${fileExt}`;
    }

    return _fileName;
  };

  /*   Icons
   **************** */
  const icons = {
    xlsx: faFileExcel,
    xls: faFileExcel,
    docx: faFileWord,
    doc: faFileWord,

    pdf: faFilePdf,
    png: faFileImage,
    jpg: faFileImageRegular,
    jpeg: faFileImageRegular,
    txt: faFileText,
    zip: faFileZipper,
    rar: faFileArchive,
  };
  const getFileExt = (fileName) => {
    // returns the file extension of the fileName
    if (!fileName) return "";
    const fileParts = fileName.split(".");
    if (fileParts.length < 2) return "";
    return fileParts[fileParts.length - 1];
  };

  const handleRenameFile = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // setLoading(true);
      const fileName = getFileName(selectedFile?.fileName);
      const length = selectedFile?.fileName.indexOf(fileName);

      const fileExt =
        "." + fileName.split(".")[(fileName?.split(".")).length - 1];

      let filePath = selectedFile?.fileName.slice(0, length);
      let oldFileName = selectedFile?.fileName;
      let updatedFileName = filePath + newFileName + fileExt;
      const { data } = await API.post(
        "/manufacturer/product-files/rename",
        {
          oldFileName,
          newFileName: updatedFileName,
          id: selectedFile?.id,
        },
        { headers: authHeader() },
      );
      // Process rename success
      getMProductFiles();
      setLoading(false);
      setShowRenameModal(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  // Sorting Up
  const sortUp = async (id) => {
    const index = mProductFiles.findIndex((item) => item.id === id);
    if (index === 0) return; // Cannot sort up the first item
    const currentSortId = mProductFiles[index].sortId;
    const currentId = mProductFiles[index].id;
    const upSortId = mProductFiles[index - 1].sortId;
    const upId = mProductFiles[index - 1].id;
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer/product-files/sortup",
        { currentSortId, currentId, upSortId, upId },
        { headers: authHeader() },
      );
      if (data.success) {
        getMProductFiles();
        setLoading(false);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  // Sorting Down
  const sortDown = async (id) => {
    const index = mProductFiles.findIndex((item) => item.id === id);
    if (index === mProductFiles.length - 1) return; // Prevent sorting down
    const currentSortId = mProductFiles[index].sortId;
    const currentId = mProductFiles[index].id;
    const downSortId = mProductFiles[index + 1].sortId;
    const downId = mProductFiles[index + 1].id;
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer/product-files/sortDown",
        { currentSortId, currentId, downSortId, downId },
        { headers: authHeader() },
      );
      if (data.success) {
        getMProductFiles();
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    setFiles((prev) =>
      prev?.filter((item) => uploadedIDList.includes(item.id)),
    );
  }, [uploadedIDList]);

  useEffect(() => {
    if (mProductFiles && mProductFiles.length > 0) {
      setLastSortId(
        mProductFiles[mProductFiles.length - 1]?.sortId
          ? mProductFiles[mProductFiles.length - 1]?.sortId
          : 0,
      );
    }
  }, [mProductFiles]);
  useEffect(() => {
    let timeout = setTimeout(() => {
      getMProductFiles();
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  /*   Table functions
   ********************************************* */
  const btn1 = () => (
    <div>
      <>
        <button
          onClick={() => setShowAddModal(true)}
          type="button"
          className="btn btn-primary ms-1"
        >
          ADD FILES
        </button>
      </>
    </div>
  );

  const buttons = () => (
    <div className="d-flex justify-content-end">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy
          ? filterBy.toUpperCase().replaceAll("_", " ")
          : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button onClick={() => setFilterBy(null)} className="dropdown-item">
          Filter by All
        </button>
      </div>
    </div>
  );
  const table_head = ["File Name", "Actions"];

  const table_body = (item, index) => (
    <tr key={item?.id}>
      <td className="d-flex align-items-center gap-2" style={{ width: "100%" }}>
        <span>
          <FontAwesomeIcon
            style={{ fontSize: 25 }}
            icon={icons[getFileExt(item?.fileName)] || faCircleQuestion}
          />
        </span>
        <span>{fileNameDisplay(item?.fileName)}</span>
      </td>
      <td className="">
        <div
          onClick={() => {
            item.sortId !== 1 && sortUp(item.id);
          }}
          className="action-icon"
        >
          <i className="mdi mdi-chevron-double-up"></i>
        </div>
        <div
          role="button"
          onClick={() => {
            item.sortId !== mProductFiles?.length && sortDown(item.id);
          }}
          className="action-icon"
        >
          <i className="mdi mdi-chevron-double-down"></i>
        </div>
        <div
          onClick={async () => {
            setSelectedFile(item);
            let fileNameWithExt = await getFileName(item?.fileName);
            setNewFileName(
              fileNameWithExt.slice(0, fileNameWithExt.length - 4),
            );
            setShowRenameModal(true);
          }}
          className="btn btn-success btn-sm"
        >
          RN
        </div>
        <div
          className="btn btn-success btn-sm px-1 mx-1"
          onClick={() => downloadFile(item.fileName)}
        >
          Download
        </div>

        <div
          onClick={() => {
            setShowDeleteModal(true);
            setDeleteID({ id: item?.id, file: item.fileName });
          }}
          className="action-icon"
        >
          <i className="mdi mdi-delete"></i>
        </div>
      </td>
    </tr>
  );

  return (
    <div>
      {/*   Table
       ********************************************* */}
      {loading && <Spinner />}
      <div
        className="px-1 flex-grow-1 overflow-y-auto d-flex"
        style={{ height: "78vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <Table
            filterBy={filterBy}
            buttons={buttons}
            btn1={btn1}
            table_head={table_head}
            table_body={table_body}
            table_data={mProductFiles || []}
          />
        </div>
      </div>

      {/*   ADD Modal
       ********************************************* */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3"
          style={{ borderRadius: 15 }}
        >
          <div>
            <h4>ADD FILES</h4>
          </div>
          <hr className="mt-0" />
          <form onSubmit={handleUploadFiles}>
            <div className="row mb-1">
              <div className="col-12">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="form-control"
                  placeholder="Enter id"
                />
              </div>
            </div>

            <div className="py-2">
              {files &&
                files.map((file, index) => (
                  <div
                    key={file.id}
                    className="bg-white px-2 rounded mb-2 d-flex justify-content-between align-items-center"
                    style={{
                      boxShadow:
                        "rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
                    }}
                  >
                    <div>{file.file.name}</div>
                    <i
                      onClick={() => {
                        setFiles((prev) =>
                          prev?.filter((item) => file.id !== item.id),
                        );
                      }}
                      className="ri-close-circle-fill text-danger fs-3"
                    ></i>
                  </div>
                ))}
            </div>

            {/*   buttons
             ********************************************* */}
            <div className="my-2">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setFiles(null);
                    setUploadedIDList([]);
                    setLastSortId(null);
                    setLoading(false);
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  ADD
                </button>
              </div>
            </div>
          </form>
          {loading && <Spinner />}
        </Modal.Body>
      </Modal>

      {/*   Rename Modal
       ********************************************* */}
      <Modal
        show={showRenameModal}
        contentClassName="bg-transparent"
        onHide={() => setShowRenameModal(false)}
      >
        <Modal.Body className="bg-white custom-border-radius">
          <div className="px-2">
            <h4>Rename File</h4>
            <h5 className="my-2">
              {getFileName(selectedFile?.fileName)?.slice(0, 30)}
            </h5>
            <input
              onChange={(e) => {
                setNewFileName(e.target.value);
              }}
              value={newFileName}
              placeholder="Enter new filename"
              className="form-control"
            />
            {/*   buttons
             ********************************************* */}
            <div className="my-2">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowRenameModal(false);
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  onClick={handleRenameFile}
                  className="btn btn-primary btn-sm ms-1"
                  type="button"
                >
                  Rename
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/*   Delete Modal
       ********************************************* */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Body className="bg-danger">
          <div>
            <div className="text-center text-white">
              <i className="ri-close-circle-line h1"></i>
              <h4 className="mt-2">Confirm Delete!</h4>
              <p className="mt-3">Do You want to delete ?</p>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-light my-2 mx-2"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-outline-light my-2"
                data-bs-dismiss="modal"
                onClick={() => {
                  deleteFile(deleteID);
                }}
              >
                DELETE
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default MProductFiles;
